import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { MapProvider } from './components/maps/MapProvider';
import { fojournTheme } from './theme/fojournTheme';
import analytics from './services/analytics';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import HomePage from './pages/HomePage';
import Dashboard from './pages/Dashboard';
import MapView from './pages/MapView';
import EntryDetail from './pages/EntryDetail';
import Search from './pages/Search';
import Profile from './pages/Profile';
import Calendar from './pages/Calendar';
import Journeys from './pages/Journeys';
import Dreams from './pages/Dreams';
import Badges from './pages/Badges';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import StaticPageView from './pages/StaticPageView';
import PublicProfile from './pages/PublicProfile';
import PublicMemoryView from './pages/PublicMemoryView';
import AdminPanel from './pages/AdminPanel';
import AdminMemoryTypes from './pages/AdminMemoryTypes';
import MapboxTestPage from './pages/MapboxTestPage';
import UniversalMapTestPage from './pages/UniversalMapTestPage';
import ChecklistsPage from './pages/ChecklistsPage';
import TemplateLibraryPage from './pages/TemplateLibraryPage';
import ChecklistManager from './components/ChecklistManager';
import ChecklistView from './components/ChecklistView';
import ChecklistPrintView from './components/ChecklistPrintView';
import StaticMapTest from './components/StaticMapTest';
import MapCalibrationDemo from './components/maps/MapCalibrationDemo';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Navbar from './components/Navbar';
import Loading from './components/Loading';
import UserBlogs from './components/UserBlogs';
import AdminRoute from './components/AdminRoute';
import AppTour from './components/AppTour';
import { useTour } from './hooks/useTour';
import './App.css';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// App Routes Component
const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { showTour, closeTour, completeTour, startTour } = useTour();

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="App">
      <Navbar onStartTour={startTour} />
      <Routes>
        {/* Public routes */}
        <Route 
          path="/login" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} 
        />
        <Route 
          path="/register" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />} 
        />
        <Route 
          path="/forgot-password" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} 
        />
        <Route 
          path="/reset-password/:token" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ResetPassword />} 
        />
        
        {/* Public profile routes (no authentication required) */}
        <Route path="/u/:username" element={<PublicProfile />} />
        <Route path="/u/:username/memory/:slug" element={<PublicMemoryView />} />
        
        {/* Public checklist sharing route */}
        <Route path="/checklists/shared/:token" element={<ChecklistView />} />
        
        {/* Protected routes */}
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <Calendar />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/map"
          element={
            <ProtectedRoute>
              <MapView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/entry/:id"
          element={
            <ProtectedRoute>
              <EntryDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <Search />
            </ProtectedRoute>
          }
        />
        <Route
          path="/journeys"
          element={
            <ProtectedRoute>
              <Journeys />
            </ProtectedRoute>
          }
        />
        <Route
          path="/journeys/:id"
          element={
            <ProtectedRoute>
              <Journeys />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dreams"
          element={
            <ProtectedRoute>
              <Dreams />
            </ProtectedRoute>
          }
        />
        <Route
          path="/badges"
          element={
            <ProtectedRoute>
              <Badges />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checklists"
          element={
            <ProtectedRoute>
              <ChecklistsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checklists/:id"
          element={
            <ProtectedRoute>
              <ChecklistView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checklists/:id/print"
          element={
            <ProtectedRoute>
              <ChecklistPrintView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates"
          element={
            <ProtectedRoute>
              <TemplateLibraryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-blogs"
          element={
            <ProtectedRoute>
              <UserBlogs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/memory-types"
          element={
            <AdminRoute>
              <AdminMemoryTypes />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/static-map-test"
          element={
            <AdminRoute>
              <StaticMapTest />
            </AdminRoute>
          }
        />
        <Route
          path="/test/map-calibration"
          element={
            <ProtectedRoute>
              <MapCalibrationDemo />
            </ProtectedRoute>
          }
        />
        <Route
          path="/test/mapbox"
          element={
            <ProtectedRoute>
              <MapboxTestPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/test/universal-map"
          element={
            <ProtectedRoute>
              <UniversalMapTestPage />
            </ProtectedRoute>
          }
        />
        
        {/* Public blog routes - accessible without authentication */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:userSlug/:postSlug" element={<BlogPost />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        
        {/* Static page routes - accessible without authentication */}
        <Route path="/:slug" element={<StaticPageView />} />
        
        {/* Default redirect */}
        <Route
          path="/"
          element={<Navigate to={isAuthenticated ? "/home" : "/landing"} replace />}
        />
        
        {/* Landing page for non-authenticated users */}
        <Route 
          path="/landing" 
          element={isAuthenticated ? <Navigate to="/home" replace /> : <LandingPage />} 
        />
        
        {/* 404 fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* App Tour for authenticated users */}
      {isAuthenticated && (
        <AppTour
          open={showTour}
          onClose={closeTour}
          onComplete={completeTour}
        />
      )}
    </div>
  );
};

function App() {
  // Initialize analytics tracking
  useEffect(() => {
    analytics.startAutoTracking();
  }, []);

  return (
    <HelmetProvider>
      {/* Default meta tags - will be overridden by individual pages */}
      <Helmet>
        <title>Fojourn - Your Travel Memory Journal</title>
        <meta name="description" content="Document your travel memories with photos, notes, and locations. Share your adventures with friends and family." />
        <meta property="og:title" content="Fojourn - Your Travel Memory Journal" />
        <meta property="og:description" content="Document your travel memories with photos, notes, and locations. Share your adventures with friends and family." />
        <meta name="twitter:title" content="Fojourn - Your Travel Memory Journal" />
        <meta name="twitter:description" content="Document your travel memories with photos, notes, and locations. Share your adventures with friends and family." />
      </Helmet>
      
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={fojournTheme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CssBaseline />
            <AuthProvider>
              <NotificationProvider>
                <MapProvider>
                  <Router>
                    <AppRoutes />
                  </Router>
                </MapProvider>
              </NotificationProvider>
            </AuthProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;


